import {
  Modal,
  Box,
  IconButton,
  Fade,
  Backdrop,
  Typography,
} from "@mui/material";
import { Close, NavigateBefore, NavigateNext } from "@mui/icons-material";

interface ImageModalProps {
  open: boolean;
  onClose: () => void;
  images?: string[];
  currentIndex: number;
  onIndexChange: (direction: "prev" | "next") => void;
  productTitle?: string;
  mainImage?: string;
}

export const ImageModal = ({
  open,
  onClose,
  images,
  currentIndex,
  onIndexChange,
  productTitle,
  mainImage,
}: ImageModalProps) => {
  const hasMultipleImages = (images?.length ?? 0) > 1;
  const currentImage = images?.[currentIndex] || mainImage;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
            maxWidth: "1200px",
            maxHeight: "90vh",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: -50,
              right: 0,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>

          {/* Navigation Buttons */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={() => onIndexChange("prev")}
                sx={{
                  position: "absolute",
                  left: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  zIndex: 1,
                  display: { xs: "none", md: "flex" },
                }}
              >
                <NavigateBefore />
              </IconButton>
              <IconButton
                onClick={() => onIndexChange("next")}
                sx={{
                  position: "absolute",
                  right: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  zIndex: 1,
                  display: { xs: "none", md: "flex" },
                }}
              >
                <NavigateNext />
              </IconButton>
            </>
          )}

          {/* Modal Image */}
          <Box
            component="img"
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            src={currentImage}
            alt={productTitle}
          />

          {/* Image Counter */}
          {hasMultipleImages && (
            <Typography
              variant="body2"
              sx={{
                color: "white",
                marginTop: 2,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                padding: "8px 16px",
                borderRadius: 2,
                fontSize: { xs: 12, sm: 14 },
              }}
            >
              {currentIndex + 1} of {images?.length || 0}
            </Typography>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};
